import type { HandlerFunction } from "../types";
import { useBillingStore } from "~/stores/billing";
import { useAuthStore } from "~/stores/auth";
import { useReportsStore } from "~/stores/reports";
import { useCommunicationStore } from "~/stores/communication";
import { useInviteStore } from "~/stores/invite";
import { useRequirementsStore } from "~/stores/requirements";
import { useTrainingStore } from "~/stores/training";
import { useSupervisionStore } from "~/stores/supervision";

/**
 * User message handler
 *
 * Handles user-specific SSE events including:
 * - Notifications and task assignments
 * - Store updates
 * - Family group updates
 * - Report generation
 * - Invites and email verification
 * - Communication (notif.create)
 */

export const handleUserMessage: HandlerFunction = (data: {
  type: string;
  message: string;
  refresh: boolean;
  data: any;
  staffReq?: any;
  training?: any;
  supervision?: any;
}) => {
  const toast = useToast();
  const billing = useBillingStore();
  const auth = useAuthStore();
  const reportsStore = useReportsStore();
  const communicationStore = useCommunicationStore();

  switch (data.type) {
    case "logout":
      auth.logout();
      break;
    case "notification":
      // Show user notification
      break;

    case "profile_update":
      // Refresh user profile
      break;

    case "task_assigned":
      // Update task list
      toast.add({
        title: "Success",
        description: data.message,
        color: "group_blue",
        icon: "i-heroicons-check-circle",
      });
      break;

    case "store":
      if (data.refresh) billing.needRefresh = true;
      if (data.message)
        toast.add({
          title: "Success",
          description: data.message,
          color: "success",
          icon: "i-heroicons-bell",
        });
      break;

    case "family_group":
      if (data.refresh) auth.me(true);
      if (data.message)
        toast.add({
          title: "Success",
          description: data.message,
          color: "success",
          icon: "i-heroicons-bell",
        });
      break;

    case "report.generated":
      // Trigger refresh - reports page will automatically unlock when status changes
      if (data.refresh) reportsStore.needRefresh = true;
      if (data.message)
        toast.add({
          title: "PDF Generated",
          description: data.message,
          color: "success",
          icon: "i-heroicons-chart-pie",
        });
      break;

    case "invite:received": {
      const inviteStore = useInviteStore();
      if (data.refresh) inviteStore.getInvitations();
      if (data.message)
        toast.add({
          title: "Success",
          description: data.message,
          color: "success",
          icon: "i-heroicons-envelope",
        });
      break;
    }

    case "email:verified": {
      if (data.refresh) auth.me(true);
      if (data.message) {
        toast.add({
          title: "Success",
          description: data.message,
          color: "success",
          icon: "i-heroicons-envelope",
        });
      }
      break;
    }

    case "notif.create": {
      // Handle incoming notification from notification service

      // Add to local communication store
      if (data.data) {
        communicationStore.addCommunicationLocal(data.data);
      }

      // Show toast notification with download action for PDF-ready notifications
      const pdfUrl = data.data?.data?.pdfUrl;
      if (pdfUrl) {
        toast.add({
          title: data.message || "PDF Ready",
          description: data.data?.body || "Your PDF has been generated and is ready to download.",
          color: "success",
          icon: "i-heroicons-document-arrow-down",
          duration: 15000,
          actions: [
            {
              label: "Download",
              color: "neutral",
              variant: "outline",
              onClick: () => {
                window.open(pdfUrl, "_blank");
              },
            },
            {
              label: "Dismiss",
              color: "neutral",
              variant: "ghost",
            },
          ],
        });
      } else {
        toast.add({
          title: data.message || "New Notification",
          description: data.data?.body || "",
          color: "primary",
          icon: "i-heroicons-envelope",
          duration: 5000,
        });
      }
      break;
    }

    case "requirement.reviewed": {
      const requirementsStore = useRequirementsStore();
      if (data.staffReq) {
        requirementsStore.updateMyRequirementLocal(data.staffReq);
      }
      break;
    }

    case "training.verified": {
      const trainingStore = useTrainingStore();
      if (data.training) {
        trainingStore.updateMyTrainingLocal(data.training);
      }
      break;
    }

    case "supervision.assigned": {
      const supervisionStore = useSupervisionStore();
      if (data.supervision) {
        supervisionStore.addMySupervisionLocal(data.supervision);
      }
      break;
    }

    case "supervision.acknowledged": {
      const supervisionStore = useSupervisionStore();
      if (data.supervision) {
        supervisionStore.updateMySupervisionLocal(data.supervision);
      }
      break;
    }

    default:
      console.log("Unknown user message type:", data.type);
  }
};
